import React, { useState } from 'react';
import { Dialog, DialogProps, Spinner } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { refreshCurrentLegacyFrame } from '@economic/react-app';

import { SalesDocument } from '../../types';
import SelectedOrdersTable from './SelectedOrdersTable';
import TransferToInvoicingForm from './TransferToInvoicingForm';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import useQuery from '../../../../../shared/hooks/useQuery';
import { ErrorDialog } from '../../../../../shared/components/modals/ErrorDialog';
import InfoDialog from '../../../../../shared/components/modals/InfoDialog';

interface TransferToInvoicingDialogProps extends Partial<DialogProps> {
    salesDocuments?: SalesDocument[];
    onSubmitted?: () => void;
}

interface TransferToInvoicingProps {
    salesDocuments?: SalesDocument[];
    onSubmitted?: () => void;
    removedOrders: number[] | null;
    setRemovedOrders: React.Dispatch<React.SetStateAction<number[] | null>>;
    navigate: NavigateFunction;
    close: () => void;
}

export function TransferToInvoicing({
    salesDocuments,
    onSubmitted,
    removedOrders,
    setRemovedOrders,
    navigate,
    close,
}: TransferToInvoicingProps) {
    const location = useLocation();

    const [navigateToInvoicesAfterSubmit, setNavigateToInvoicesAfterSubmit] = useState<boolean>(false);

    const salesDocumentIds = salesDocuments
        ? salesDocuments.map(order => order.salesDocumentId)
        : location.state?.draftIds
        ? location.state?.draftIds
        : [];

    const {
        data: verificationErrors,
        loading: verifyQueryLoading,
        error: verifyQueryError,
    } = useQuery<string[]>(`/sales/order/transfer/invoicing/verify?salesDocumentIds=${salesDocumentIds}`, [], {
        skip: salesDocumentIds.length === 0,
    });

    const ordersQuery = useQuery<SalesDocument[]>(`/sales/order/multiple?salesDocumentIds=${salesDocumentIds}`, [], {
        skip: salesDocuments !== undefined,
    });

    const orders = salesDocuments ?? ordersQuery.data;

    const selectedOrders = orders.filter(order => !removedOrders?.includes(order.number));

    const customersOnSelectedOrders = new Set(selectedOrders.map(order => order.customerId));
    const hasSameCustomerOnMultipleOrders = customersOnSelectedOrders.size !== selectedOrders.length;

    const handleRemoveRow = (number: number) => {
        if (removedOrders === null) {
            setRemovedOrders([number]);
        } else {
            const newValue = removedOrders.slice();
            newValue.push(number);
            setRemovedOrders(newValue);
        }
    };

    async function submit(values) {
        const salesDocumentIds = selectedOrders.map(order => order.salesDocumentId);
        try {
            const response = await internalApiClient.post(`/sales/order/transfer/invoicing`, salesDocumentIds, {
                params: {
                    combined: hasSameCustomerOnMultipleOrders ? values.combined : false,
                    showHeading: values.showHeading,
                },
            });
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    const handleOnSubmitted = (close: () => void) => {
        if (navigateToInvoicesAfterSubmit) {
            navigate('/sales/invoicing/invoices');
        } else {
            if (onSubmitted) {
                onSubmitted();
            } else {
                refreshCurrentLegacyFrame('content');
            }
            close();
        }
    };

    if (salesDocumentIds.length === 0) {
        return (
            <InfoDialog label={t`Transfer to invoice`} closeText={t`Close`} onClose={close}>
                <Trans>Select one or more orders before transferring.</Trans>
            </InfoDialog>
        );
    }
    if (verifyQueryLoading) {
        return <Spinner />;
    }
    if (verifyQueryError) {
        return <ErrorDialog onClose={close} />;
    }
    if (verificationErrors && verificationErrors.length > 0) {
        return (
            <InfoDialog label={t`Cannot transfer to invoicing`} closeText={t`Close`} onClose={close}>
                <ul>
                    {verificationErrors.map(text => (
                        <li key={text}>{text}</li>
                    ))}
                </ul>
            </InfoDialog>
        );
    }
    return (
        <>
            <Dialog.Title>
                <Trans>Transfer to invoice</Trans>
            </Dialog.Title>
            <SelectedOrdersTable selectedOrders={selectedOrders} handleRemoveOrder={handleRemoveRow} />
            <TransferToInvoicingForm
                formKey={hasSameCustomerOnMultipleOrders ? 1 : 2}
                submit={submit}
                onSubmitted={() => handleOnSubmitted(close)}
                disableSingleInvoiceControls={!hasSameCustomerOnMultipleOrders}
                selectedOrdersCount={selectedOrders.length}
                setNavigateToInvoicesAfterSubmit={value => setNavigateToInvoicesAfterSubmit(value)}
            />
        </>
    );
}

export default function TransferToInvoicingDialog({
    salesDocuments,
    onSubmitted,
    ...dialogProps
}: TransferToInvoicingDialogProps) {
    const navigate = useNavigate();
    const [removedOrders, setRemovedOrders] = useState<number[] | null>(null);
    return (
        <Dialog
            onClose={() => {
                setRemovedOrders(null);
                // handle closing dialog from legacy page
                if (location.hash) {
                    navigate(location.pathname);
                }
            }}
            draggable
            {...dialogProps}>
            <Dialog.Content aria-label="transfer-order-to-invoicing-dialog">
                {({ close }) => (
                    <TransferToInvoicing
                        salesDocuments={salesDocuments}
                        onSubmitted={onSubmitted}
                        removedOrders={removedOrders}
                        setRemovedOrders={setRemovedOrders}
                        navigate={navigate}
                        close={close}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
}
